import React, {useEffect} from 'react'
import {createPortal} from 'react-dom'
import styled, {keyframes} from 'styled-components'
import {X} from 'lucide-react'
import {Tooltip} from '../BaseComponents'
import {EllipsisText} from './Table'

/**
 * Drawer
 *
 * A right-anchored slide-in panel + backdrop (portaled to <body>). Role-agnostic
 * detail/side panel — used for the platform Branch Details view, and reusable
 * anywhere a side panel reads better than a centered Modal. Escape and backdrop
 * click both close it.
 */

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  /** Panel width in px (capped at 92vw on small screens). Default 480. */
  width?: number
  children: React.ReactNode
  footer?: React.ReactNode
  'data-testid'?: string
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`
const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: var(--z-modal);
  animation: ${fadeIn} 0.2s ease;
`

const Panel = styled.aside<{$width: number}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(${({$width}) => $width}px, calc(100vw - var(--space-8)));
  background: var(--bg-elevated);
  box-shadow: var(--shadow-modal);
  z-index: var(--z-modal);
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.25s ease;

  /* Phones: no sliver of page beside the panel, it is the whole screen. */
  @media (max-width: 620px) {
    width: 100vw;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-5) var(--space-6);
  border-bottom: 1px solid var(--border-subtle);
`

/* overflow: hidden lets the heading shrink in the header row; the EllipsisText inside cuts a
   long title with "…", and only then does its tooltip show the whole title (STFFLG-4236). */
const Title = styled.h2`
  margin: 0;
  min-width: 0;
  font-size: var(--fs-h3);
  font-weight: var(--fw-semibold);
  color: var(--primary-800);
  overflow: hidden;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--space-6);
`

const Footer = styled.div`
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--border-subtle);
`

export function Drawer({
  open,
  onClose,
  title,
  width = 480,
  children,
  footer,
  'data-testid': dataTestId,
}: DrawerProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <>
      <Backdrop onClick={onClose} data-testid={dataTestId ? `${dataTestId}-backdrop` : undefined} />
      <Panel
        $width={width}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={dataTestId}>
        <Header>
          <Title>
            <Tooltip content={title} block>
              <EllipsisText>{title}</EllipsisText>
            </Tooltip>
          </Title>
          <CloseButton onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden="true" />
          </CloseButton>
        </Header>
        <Body>{children}</Body>
        {footer ? <Footer>{footer}</Footer> : null}
      </Panel>
    </>,
    document.body,
  )
}
